(function () {
  "use strict";

  a3_cabin_reports.factory("LotteryFactory", LotteryFactory);

  /**
   * @memberof CabinReports
   * @ngdoc factory
   * @name LotteryFactory
   * @description
   *   Stores, retrieves and filters Lotteries for flights.
   * @ngInject
   */
  function LotteryFactory(GenericService) {
    /**
     * Initialize the Lottery model
     * @memberof LotteryFactory
     */
    function Lottery() {
      this.Id = null;
      this.Name = null;
      this.LotteryMode = null; // 1='AllFlights' | 2='SpecificFlightNumbers' | 3='SpecificRoutes'
      this.Active = null;
      this.DateFrom = null;
      this.DateTo = null;
      this.FlightNumbers = null; // array of strings, e.g. ["A3747"] — used when LotteryMode = 2
      this.FlightRoutes = null; // array of { Departure, Arrival } objects — used when LotteryMode = 3
      this.Announcement = null;
      this.Gifts = null; // array of { Id, Description, NumberOfWinners, NumberOfSubstitutes }
      this.PassengersType = null;
      this.GetId = function () {
        return this.Id;
      };
    }

    /**
     * Initialize the SubmittedLottery model
     * @memberof LotteryFactory
     */
    function SubmittedLottery() {
      this.Id = null; // composite: LotteryId + '_' + FlightId
      this.LotteryId = null;
      this.FlightId = null;
      this.GetId = function () {
        return this.Id;
      };
    }

    var lotteryObjType = "LotteryItems";
    var submittedObjType = "SubmittedLotteries";
    var draftObjType = "LotteryDraftResults";
    var pendingObjType = "LotteryPendingResults";

    /**
     * Initialize the LotteryDraftResult model
     */
    function LotteryDraftResult() {
      this.Id = null; // composite: lotteryId + '_' + flightId
      this.LotteryId = null;
      this.FlightId = null;
      this.GiftResults = null;
      this.SavedAt = null;
      this.GetId = function () {
        return this.Id;
      };
    }

    /**
     * Initialize the LotteryPendingResult model (queued for backend sync)
     */
    function LotteryPendingResult() {
      this.Id = null; // composite: lotteryId + '_' + flightId
      this.LotteryId = null;
      this.FlightId = null;
      this.GiftResults = null;
      this.SubmittedAt = null;
      this.CrewMemberAimsId = null;
      this.FlightDisplay = null;
      this.GetId = function () {
        return this.Id;
      };
    }

    var service = {
      getLotteriesFromStorage: getLotteriesFromStorage,
      setLotteriesFromServer: setLotteriesFromServer,
      getLotteryById: getLotteryById,
      getApplicableLotteries: getApplicableLotteries,
      getSubmittedLotteriesFromStorage: getSubmittedLotteriesFromStorage,
      setSubmittedLotteriesFromServer: setSubmittedLotteriesFromServer,
      // Draft & submission
      saveDraftResult: saveDraftResult,
      getDraftResult: getDraftResult,
      removeDraftResult: removeDraftResult,
      submitResult: submitResult,
      getPendingResults: getPendingResults,
      removePendingResult: removePendingResult,
      hasPendingResult: hasPendingResult,
    };

    return service;

    // ─── Storage ────────────────────────────────────────────────────────────────

    /**
     * Get all lotteries from local storage
     * @memberof LotteryFactory
     * @returns {Array} lotteries
     */
    function getLotteriesFromStorage() {
      return GenericService.list(lotteryObjType, Lottery);
    }

    /**
     * Persist lotteries received from the server
     * @memberof LotteryFactory
     * @param {Array} lotteries
     */
    function setLotteriesFromServer(lotteries) {
      if (!lotteries) return;
      GenericService.saveAll(lotteries, lotteryObjType, Lottery);
    }

    /**
     * Get a single lottery by Id
     * @memberof LotteryFactory
     * @param {number|string} id
     * @returns {object|null} lottery
     */
    function getLotteryById(id) {
      return GenericService.get(id, lotteryObjType, Lottery);
    }

    // ─── Applicability filtering ─────────────────────────────────────────────────

    /**
     * Check whether the flight date falls within the lottery's active period
     * @memberof LotteryFactory
     * @param {object} lottery
     * @param {Date} flightDate
     * @returns {boolean}
     */
    function isWithinActivePeriod(lottery, flightDate) {
      if (!lottery.Active) return false;

      if (!flightDate) return false;
      var date = new Date(flightDate);
      if (isNaN(date.getTime())) return false;
      date.setHours(0, 0, 0, 0);

      var from = lottery.DateFrom ? new Date(lottery.DateFrom) : null;
      var to = lottery.DateTo ? new Date(lottery.DateTo) : null;

      if (from) from.setHours(0, 0, 0, 0);
      if (to) to.setHours(0, 0, 0, 0);

      var afterFrom = !from || date >= from;
      var beforeTo = !to || date <= to;

      return afterFrom && beforeTo;
    }

    /**
     * Return all lotteries that are applicable to the given flight
     * @memberof LotteryFactory
     * @param {object} flight
     * @returns {Array} applicableLotteries
     */
    function getApplicableLotteries(flight) {
      if (!flight) return [];

      var lotteries = getLotteriesFromStorage();
      var flightDate = flight.ScheduledDeparture || flight.ScheduledDepartureDateUTC || null;

      // Normalize flight values once — trim + uppercase
      var flightNo = (flight.FlightNo || "").trim().toUpperCase();
      var flightRoute = (
        (flight.Departure || "").trim() +
        "-" +
        (flight.Arrival || "").trim()
      ).toUpperCase();

      return lotteries.filter(function (lottery) {
        if (!isWithinActivePeriod(lottery, flightDate)) return false;

        // LotteryMode arrives as a numeric enum (1/2/3) but guard strings too
        var mode = lottery.LotteryMode;
        switch (mode) {
          case 1:
          case "AllFlights":
            return true;

          case 2:
          case "SpecificFlightNumbers":
            return (
              lottery.FlightNumbers &&
              lottery.FlightNumbers.some(function (fn) {
                return fn.trim().toUpperCase() === flightNo;
              })
            );

          case 3:
          case "SpecificRoutes":
            return (
              lottery.FlightRoutes &&
              lottery.FlightRoutes.some(function (r) {
                // server may send a string "ATH-SKG" or an object { Departure, Arrival }
                var normalized =
                  typeof r === "object" && r !== null
                    ? ((r.Departure || "").trim() + "-" + (r.Arrival || "").trim()).toUpperCase()
                    : r.trim().toUpperCase();
                return normalized === flightRoute;
              })
            );

          default:
            return false;
        }
      });
    }

    // ─── Submitted lotteries ─────────────────────────────────────────────────────

    /**
     * Get all submitted lottery records from local storage
     * @memberof LotteryFactory
     * @returns {Array} submittedLotteries
     */
    function getSubmittedLotteriesFromStorage() {
      return GenericService.list(submittedObjType, SubmittedLottery);
    }

    /**
     * Persist submitted lottery records received from the server
     * @memberof LotteryFactory
     * @param {Array} list  Array of { LotteryId, FlightId }
     */
    function setSubmittedLotteriesFromServer(list) {
      if (!list) return;

      var items = list.map(function (item) {
        var s = new SubmittedLottery();
        s.Id = item.LotteryId + "_" + item.FlightId;
        s.LotteryId = item.LotteryId;
        s.FlightId = item.FlightId;
        return s;
      });

      GenericService.saveAll(items, submittedObjType, SubmittedLottery);
    }

    // ─── Draft results ────────────────────────────────────────────────────────────

    /**
     * Save a lottery draft result to local storage (overwrites existing for same key).
     * @memberof LotteryFactory
     * @param {{id, lotteryId, flightId, giftResults, savedAt}} draft
     */
    function saveDraftResult(draft) {
      if (!draft || !draft.id) return;
      var all = GenericService.list(draftObjType, LotteryDraftResult) || [];
      var idx = -1;
      for (var i = 0; i < all.length; i++) {
        if (all[i].Id === draft.id) {
          idx = i;
          break;
        }
      }
      var item = new LotteryDraftResult();
      item.Id = draft.id;
      item.LotteryId = draft.lotteryId;
      item.FlightId = draft.flightId;
      item.GiftResults = draft.giftResults;
      item.SavedAt = draft.savedAt || new Date().toISOString();
      if (idx > -1) {
        all[idx] = item;
      } else {
        all.push(item);
      }
      GenericService.saveAll(all, draftObjType, LotteryDraftResult);
    }

    /**
     * Retrieve a lottery draft result from local storage.
     * @memberof LotteryFactory
     * @param {number} lotteryId
     * @param {number} flightId
     * @returns {object|null}
     */
    function getDraftResult(lotteryId, flightId) {
      var compositeId = lotteryId + "_" + flightId;
      return GenericService.get(compositeId, draftObjType, LotteryDraftResult);
    }

    /**
     * Remove a lottery draft result from local storage.
     * @memberof LotteryFactory
     * @param {number} lotteryId
     * @param {number} flightId
     */
    function removeDraftResult(lotteryId, flightId) {
      var compositeId = lotteryId + "_" + flightId;
      var all = GenericService.list(draftObjType, LotteryDraftResult) || [];
      var filtered = all.filter(function (d) {
        return d.Id !== compositeId;
      });
      GenericService.saveAll(filtered, draftObjType, LotteryDraftResult);
    }

    // ─── Pending results (queued for backend sync) ────────────────────────────────

    /**
     * Submit a completed lottery result: marks as submitted locally and queues for sync.
     * @memberof LotteryFactory
     * @param {{id, lotteryId, flightId, giftResults, submittedAt}} result
     */
    function submitResult(result) {
      if (!result || !result.id) return;

      // 1. Persist in pending queue (picked up by SyncAPIService on next sync)
      var pending = GenericService.list(pendingObjType, LotteryPendingResult) || [];
      var pendingIdx = -1;
      for (var i = 0; i < pending.length; i++) {
        if (pending[i].Id === result.id) {
          pendingIdx = i;
          break;
        }
      }
      var item = new LotteryPendingResult();
      item.Id = result.id;
      item.LotteryId = result.lotteryId;
      item.FlightId = result.flightId;
      item.GiftResults = result.giftResults;
      item.SubmittedAt = result.submittedAt || new Date().toISOString();
      item.CrewMemberAimsId = result.crewMemberId || null;
      item.FlightDisplay = result.flightDisplay || null;
      if (pendingIdx > -1) {
        pending[pendingIdx] = item;
      } else {
        pending.push(item);
      }
      GenericService.saveAll(pending, pendingObjType, LotteryPendingResult);

      // 2. Track as a submitted lottery so the details page knows
      var submitted = GenericService.list(submittedObjType, SubmittedLottery) || [];
      var subIdx = -1;
      for (var j = 0; j < submitted.length; j++) {
        if (submitted[j].Id === result.id) {
          subIdx = j;
          break;
        }
      }
      if (subIdx === -1) {
        var sub = new SubmittedLottery();
        sub.Id = result.id;
        sub.LotteryId = result.lotteryId;
        sub.FlightId = result.flightId;
        submitted.push(sub);
        GenericService.saveAll(submitted, submittedObjType, SubmittedLottery);
      }
    }

    /**
     * Get all pending lottery results waiting for sync.
     * @memberof LotteryFactory
     * @returns {Array}
     */
    function getPendingResults() {
      var pending = GenericService.list(pendingObjType, LotteryPendingResult) || [];

      return pending.map(function (item) {
        var results = [];

        angular.forEach(item.GiftResults || [], function (giftResult) {
          angular.forEach(giftResult.selected || [], function (selectedRecipient) {
            if (!selectedRecipient || !selectedRecipient.pax) {
              return;
            }

            results.push({
              GiftId: giftResult.gift ? giftResult.gift.Id : null,
              PassengerFullName: selectedRecipient.pax.Fullname || null,
              PassengerPNR: selectedRecipient.pax.PNR || null,
              PassengerUCI: selectedRecipient.pax.UCI || null,
              PassengerEmailAddress: selectedRecipient.email || null,
              PassengerTelephoneNumber: selectedRecipient.phone || null,
            });
          });
        });

        return {
          FlightId: item.FlightId,
          LotteryId: item.LotteryId,
          CrewMemberAimsId: item.CrewMemberAimsId || null,
          SubmissionTimestamp: item.SubmittedAt,
          FlightDisplay: item.FlightDisplay || null,
          Results: results,
        };
      });
    }

    /**
     * Remove a pending lottery result (called after successful backend upload).
     * @memberof LotteryFactory
     * @param {string} compositeId  lotteryId + '_' + flightId
     */
    function removePendingResult(compositeId) {
      var pending = GenericService.list(pendingObjType, LotteryPendingResult) || [];
      var filtered = pending.filter(function (p) {
        return p.Id !== compositeId;
      });
      GenericService.saveAll(filtered, pendingObjType, LotteryPendingResult);
    }

    /**
     * Check if a pending result exists for the given lottery/flight composite key.
     * @memberof LotteryFactory
     * @param {string} compositeId  lotteryId + '_' + flightId
     * @returns {boolean}
     */
    function hasPendingResult(compositeId) {
      var pending = GenericService.list(pendingObjType, LotteryPendingResult) || [];
      return pending.some(function (p) {
        return p.Id === compositeId;
      });
    }
  }
})();
